import {
    Button,
    Input,
    FormControl,
    FormLabel,
    VStack,
    Box,
    HStack,
    Text
} from '@chakra-ui/react'
import { Campaign } from 'graphql/types'
import * as React from 'react'
import Editor from 'react-simple-wysiwyg'

interface Props {
    Campaign: Campaign
    handleSubmit: (CampaignID: string, message: string, subject: string, values: Value[]) => Promise<void>
}

export interface Value {
    key: string
    value: string
}

export const EditCopy = ({Campaign, handleSubmit}: Props) => {
    const [html, setHtml] = React.useState(Campaign.Message || '')
    const [subject, setSubject] = React.useState(Campaign.Title || '')
    const [values, setValues] = React.useState<Value[]>([])
    const [isSubmitting, setIsSubmitting] = React.useState(false)

    React.useEffect(() => {
        if (!Campaign.Values) {
            return
        }
        try {
            const newValues = JSON.parse(Campaign.Values)
            setValues(newValues)
        } catch (error) {
            console.error('Error parsing campaign values:', error)
        }
    }, [Campaign.Values])

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newHtml: string = e.target.value
        const foundValues = newHtml.matchAll(/{{(.*?)}}/g)
        const newValues: Value[] = [...foundValues].map((value) => {
            return {
                key: value[1],
                value: values.find((v) => v.key === value[1])?.value || ''
            }
        })
        setValues(newValues)
        setHtml(newHtml)
    }

    const handleFormSubmit = async () => {
        setIsSubmitting(true)
        try {
            await handleSubmit(Campaign.ID, html, subject, values)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <VStack spacing={4} align="stretch" p={4}>
            <FormControl>
                <FormLabel>Subject</FormLabel>
                <Input
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.currentTarget.value)}
                />
            </FormControl>

            <FormControl>
                <FormLabel>Message</FormLabel>
                <Box
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={2}
                >
                    <Editor
                        containerProps={{ style: { resize: 'both' } }}
                        value={html}
                        onChange={onChange}
                    />
                </Box>
            </FormControl>

            {values.length > 0 && (
                <Box>
                    <Text fontWeight="bold" mb={2}>
                        Template Variables
                    </Text>
                    <VStack spacing={3} align="stretch">
                        {values.map((value, index) => (
                            <FormControl key={index}>
                                <FormLabel fontWeight="semibold">{`{{${value.key}}}`}</FormLabel>
                                <Input
                                    value={value.value}
                                    onChange={(e) => {
                                        const newValues = [...values]
                                        newValues[index].value = e.currentTarget.value
                                        setValues(newValues)
                                    }}
                                    placeholder={`Value for ${value.key}`}
                                />
                            </FormControl>
                        ))}
                    </VStack>
                </Box>
            )}

            <Button
                colorScheme="blue"
                onClick={handleFormSubmit}
                isLoading={isSubmitting}
                loadingText="Saving..."
            >
                Save
            </Button>
        </VStack>
    )
}
